import { get } from 'svelte/store'
import { emulateUser } from 'stores/auth'
import auth from 'services/auth/authService'

/**
 * Gets the list of available user roles
 * @returns {Promise<any>}
 */
export const callApi = async (url, method = 'GET', body = null, withCredentials = true) => {
  let response
  ;({ response, body } = await fetchApiCall(body, url, method, withCredentials))

  const responseBody = await getResponseBody(response)

  if (response.ok) {
    return responseBody
  } else {
    handleErrorResponse(response, responseBody)
  }
}

/**
 * Calls the API with the given URL and access token and returns the response.
 * @param {string} url
 * @param {string} method
 * @returns {Promise<any>}
 */
export const callApiFiles = async (url, method = 'POST', body = null) => {
  let response
  ;({ response, body } = await fetchApiCall(body, url, method))

  const responseBody = await getResponseBody(response)
  const contentDisposition = response.headers.get('Content-Disposition')

  return { body: responseBody, contentDisposition }
}

// getTokenSilently() will return a valid token if it exists or fetch a new one if it doesn't
// The Auth0 SDK already handles the token expiration and refresh
async function getValidToken() {
  const auth0Client = await auth.createClient()
  try {
    return await auth0Client.getTokenSilently()
  } catch (error) {
    await auth.logout(true)
  }
}

async function fetchApiCall(body, url, method, withCredentials = true) {
  const headers = {
    Authorization: `Bearer ${await getValidToken()}`,
  }

  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(body)
  }

  const emulate = get(emulateUser)
  if (emulate) {
    headers['x-emulate-user'] = emulate.email
    headers['x-emulate-app'] = 'bm'
  }

  // Add the x-dev-request header to all requests to indicate that this is a development request
  // headers['x-dev-request'] = 'true'

  const fetchOptions = {
    method: method,
    headers: headers,
    body: body,
  }

  if (withCredentials) {
    fetchOptions.credentials = 'include'
  }

  const response = await fetch(url, fetchOptions)

  return { response, body }
}

/**
 * Helper function to parse response body based on content type.
 * @param {Response} response
 * @returns {Promise<any>}
 */
async function getResponseBody(response) {
  const contentType = response.headers.get('Content-Type') || ''

  // Check if the response has a body by looking at the Content-Length header
  const contentLength = response.headers.get('Content-Length')
  if (response.status === 204 || response.status === 205 || response.status === 304 || contentLength === '0') {
    return {}
  }

  if (contentType.includes('application/json')) {
    // Some backends return JSON content-type with an empty body.
    const rawBody = await response.text()
    return rawBody ? JSON.parse(rawBody) : {}
  } else if (contentType.includes('text')) {
    return await response.text()
  } else {
    return await response.blob() // Fallback for other types like binary data
  }
}

/**
 * Helper function to handle error responses and throw an appropriate error.
 * @param {Response} response
 * @param {any} responseBody
 */
function handleErrorResponse(response, responseBody) {
  const contentType = response.headers.get('Content-Type') || ''

  if (response.status === 404) {
    const errorBody = contentType.includes('application/json') ? responseBody : 'Resource not found'
    throw new Error(errorBody)
  } else {
    const errorMessage = responseBody?.message || responseBody || response.statusText
    throw new Error(`Error: ${response.status} - ${errorMessage}`)
  }
}
